import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Container } from '@core/di/Container';
import type { IAuthService, User, Session } from '@core/auth/types';
import { i18n } from '../i18n';

const SESSION_ID_KEY = 'auth_session_id';
const USER_ID_KEY = 'auth_user_id';

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<User | null>(null);
  const session = ref<Session | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => session.value !== null && currentUser.value !== null);
  const isAdmin = computed(() => currentUser.value?.role === 'ADMIN');
  const isManager = computed(() => currentUser.value?.role === 'MANAGER' || isAdmin.value);

  function getService(): IAuthService {
    return Container.getInstance().get<IAuthService>('AuthService');
  }

  async function initializeAuth(): Promise<void> {
    const sessionId = localStorage.getItem(SESSION_ID_KEY);
    const userId = localStorage.getItem(USER_ID_KEY);
    if (!sessionId || !userId) return;

    try {
      const authService = getService();
      const valid = await authService.isSessionValid(sessionId);
      if (!valid) {
        _clearStorage();
        return;
      }
      const user = await authService.getUserById(userId);
      if (!user) {
        _clearStorage();
        return;
      }
      currentUser.value = user;
      // Reconstruct a minimal session reference so isAuthenticated is true
      session.value = { id: sessionId } as Session;
    } catch {
      _clearStorage();
    }
  }

  async function login(username: string, password: string): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      const s = await getService().login(username, password);
      await _applySession(s);
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.loginFailed');
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function loginWithPin(username: string, pin: string): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      const s = await getService().loginWithPin(username, pin);
      await _applySession(s);
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.pinLoginFailed');
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      if (session.value) {
        await getService().logout(session.value.id);
      }
    } catch {
      // best-effort
    } finally {
      _clearStorage();
      currentUser.value = null;
      session.value = null;
      isLoading.value = false;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      if (!currentUser.value) throw new Error('Not authenticated');
      // Verify current password by attempting login
      await getService().login(currentUser.value.username, currentPassword);
      await getService().updateUser(currentUser.value.id, { passwordHash: newPassword });
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.changePasswordFailed');
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  function clearError(): void {
    error.value = null;
  }

  async function _applySession(s: Session): Promise<void> {
    session.value = s;
    const user = await getService().getUserById(s.userId);
    currentUser.value = user;
    localStorage.setItem(SESSION_ID_KEY, s.id);
    localStorage.setItem(USER_ID_KEY, s.userId);
  }

  function _clearStorage(): void {
    localStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(USER_ID_KEY);
  }

  return {
    currentUser,
    session,
    isLoading,
    error,
    isAuthenticated,
    isAdmin,
    isManager,
    initializeAuth,
    login,
    loginWithPin,
    logout,
    changePassword,
    clearError,
  };
});
